import React, { useState } from 'react';
import { Sparkles, Edit2, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { aiAPI, projectsAPI } from '../services/api';
import { toast } from 'sonner';

const ProjectInfo = ({ project, isAdmin, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState(project);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateDescription = async () => {
    setIsGenerating(true);
    try {
      const response = await aiAPI.generateDescription({
        project_title: editedProject.title,
        prompt: 'Générer une description professionnelle pour investisseurs',
      });
      setEditedProject({ ...editedProject, description: response.data.description });
      toast.success('Description générée avec succès!');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      if (project.id) {
        await projectsAPI.update(project.id, editedProject);
      } else {
        const response = await projectsAPI.create(editedProject);
        onUpdate(response.data);
      }
      onUpdate(editedProject);
      setIsEditing(false);
      toast.success('Projet mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50 animate-slideIn">
      <div className="flex justify-between items-start mb-4">
        <h2 className="text-2xl font-bold text-white">Information du Projet</h2>
        {isAdmin && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-project-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Sauvegarder
                </Button>
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedProject(project);
                  }}
                  size="sm"
                  variant="outline"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="edit-project-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Titre</label>
            <Input
              value={editedProject.title}
              onChange={(e) => setEditedProject({ ...editedProject, title: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="project-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <Textarea
              value={editedProject.description}
              onChange={(e) => setEditedProject({ ...editedProject, description: e.target.value })}
              rows={6}
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="project-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">URL du site</label>
            <Input
              value={editedProject.url || ''}
              onChange={(e) => setEditedProject({ ...editedProject, url: e.target.value })}
              placeholder="https://www.example.com"
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="project-url-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">URL YouTube (embed)</label>
            <Input
              value={editedProject.youtube_url || ''}
              onChange={(e) => setEditedProject({ ...editedProject, youtube_url: e.target.value })}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="project-youtube-input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Format: https://www.youtube.com/embed/VIDEO_ID
            </p>
          </div>
          <Button
            onClick={handleGenerateDescription}
            disabled={isGenerating}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="generate-description-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {isGenerating ? 'Génération...' : 'Générer avec IA'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-300 leading-relaxed" data-testid="project-description">
            {project.description}
          </p>
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-blue-400 hover:text-blue-300 transition-colors"
              data-testid="project-url-link"
            >
              Visiter le site →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectInfo;